import { useCallback, useEffect } from "react";

import { isInsideContentEditable, isUserInput } from "@skilitics/designer-ui-components";

export type ClipboardActions = {
  copy?: () => void;
  cut?: () => void;
  paste?: () => void;
  pasteReplace?: () => void;
};

export type ClipboardKeybindProps = {
  /**
   * Actions to perform for clipboard operations
   */
  actions: ClipboardActions;
  /**
   * Whether to disable keyboard shortcuts
   */
  disabled?: boolean;
};

/**
 * Hook to provide keyboard shortcuts for clipboard operations
 *
 * - Cmd/Ctrl+C: Copy
 * - Cmd/Ctrl+X: Cut
 * - Cmd/Ctrl+V: Paste (inside container or after layout slot)
 * - Cmd/Ctrl+Shift+V: Paste and replace
 */
export const useClipboardKeybinds = ({ actions, disabled = false }: ClipboardKeybindProps) => {
  const { copy, cut, paste, pasteReplace } = actions;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (disabled || isUserInput(event.target)) return;

      // Prevent copying nodes if the user has text selected.
      if (!window.getSelection()?.isCollapsed) return;

      const isMac = isMacOS();
      const modifier = isMac ? event.metaKey : event.ctrlKey;

      if (!modifier) return;

      // Don't allow node copy/paste if inside a contentEditable element.
      // Note: handleKeyDown in `useNodeRichEditableText` should stop propagation to here.
      if (isInsideContentEditable(event.target)) return;

      // Handle clipboard shortcuts
      switch (event.key) {
        case "c": // Copy
          if (!event.shiftKey && !event.altKey) {
            event.preventDefault();
            copy?.();
          }
          break;

        case "x": // Cut
          if (!event.shiftKey && !event.altKey) {
            event.preventDefault();
            cut?.();
          }
          break;

        case "v": // Paste
          if (event.shiftKey && !event.altKey) {
            // Cmd/Ctrl+Shift+V: Paste replace
            event.preventDefault();
            pasteReplace?.();
          } else if (!event.shiftKey && !event.altKey) {
            // Cmd/Ctrl+V: Paste inside container or after layout slot
            event.preventDefault();
            paste?.();
          }
          break;

        default:
          break;
      }
    },
    [copy, cut, paste, pasteReplace, disabled],
  );

  // Add and remove event listener
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);
};

// Helper function to detect macOS
const isMacOS = () => {
  return typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
};
